import React, { useState, useEffect } from "react";
import SearchInput from "../../components/SearchInput/SearchInput";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import Filter from "../../assets/svg/icons/common/Filter";
import RedDotBadge from "../../assets/svg/icons/common/RedDotBadge";
import MonthPicker from "../../components/form/month-picker";
import {
  parse,
  startOfMonth,
  endOfMonth,
  format,
  startOfYear,
  endOfYear,
  subYears,
} from "date-fns";
import ChargebackStats from "./components/ChargebackStats";
import ChargebackCharts from "./components/ChargebackCharts";
import FilterDrawer from "./components/FilterDrawer";
import useChargebackStore from "../../stores/chargeback";
import useCommonStore from "../../stores/common";
import styles from "./style.module.scss";
import ChargebackTabs from "./components/ChargebackTabs";
import MobileDesktopAlert from "../../components/MobileDesktopAlert";

const isRangeModified = (from, to, min, max) => {
  if (from == null || to == null || from === "" || to === "") return false;
  return Number(from) > min || Number(to) < max;
};

const getChargebackFilterCount = (filters, filterData) => {
  if (!filters || !filterData) return 0;

  const hasOrgFilter = filters.orgFilter !== null && filters.orgFilter !== "";
  const cardsCount = filters.cards?.length ?? 0;
  const hasCbAmountModified = isRangeModified(
    filters.amount?.from,
    filters.amount?.to,
    filterData.min_cb_amount || 0,
    filterData.max_cb_amount || 0,
  );
  const hasDaysToRebutModified = isRangeModified(
    filters.daysToRebut?.from,
    filters.daysToRebut?.to,
    filterData.min_days_to_rebut || 0,
    filterData.max_days_to_rebut || 0,
  );
  const hasDaysToRebutNull = filters.isDaysToRebutNull === true;
  const reasonsCount = filters.reasons?.length ?? 0;

  return [
    hasOrgFilter,
    cardsCount,
    hasCbAmountModified,
    hasDaysToRebutModified,
    hasDaysToRebutNull,
    reasonsCount,
  ].reduce((sum, val) => {
    if (typeof val === "number") {
      return sum + val;
    }
    return sum + (val ? 1 : 0);
  }, 0);
};

const ChargebackPage = () => {
  const reset = useChargebackStore((state) => state.reset);
  const setFilters = useChargebackStore((state) => state.setFilters);
  const filters = useChargebackStore((state) => state.filters);
  const filterData = useChargebackStore((state) => state.filterData);
  const filterCount = getChargebackFilterCount(filters, filterData);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );

  // Typed value is held locally and pushed to the store on a delay. A search
  // term makes the list query all history instead of the selected month, so
  // firing one request per keystroke is far more expensive than it used to be.
  const [searchInput, setSearchInput] = useState(filters.searchTerm ?? "");

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    if (searchInput === filters.searchTerm) return;
    const timer = setTimeout(() => {
      setFilters({
        ...filters,
        searchTerm: searchInput,
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, filters, setFilters]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleMonthPickerChange = (data) => {
    let startDate, endDate;
    const currentDate = new Date();
    let rangeType = "MTD";

    if (["MTD", "LTY", "YTD"].includes(data.month)) {
      rangeType = data.month;
      if (data.month === "YTD") {
        startDate = startOfYear(currentDate);
        endDate = currentDate;
      } else if (data.month === "MTD") {
        startDate = startOfMonth(currentDate);
        endDate = currentDate;
      } else if (data.month === "LTY") {
        const lastYearDate = subYears(currentDate, 1);
        startDate = startOfYear(lastYearDate);
        endDate = endOfYear(lastYearDate);
      }
    } else {
      rangeType = "MTD";
      const parsedDate = parse(
        `${data.month} ${data.year}`,
        "MMM yyyy",
        new Date(),
      );
      startDate = parsedDate;
      endDate = endOfMonth(parsedDate);
    }

    setFilters({
      ...filters,
      dateRange: {
        startDate: format(startDate, "MM/dd/yyyy"),
        endDate: format(endDate, "MM/dd/yyyy"),
        rangeType: rangeType,
      },
      orgFilter: filters.orgFilter,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={`${styles.page_heading} ${styles.column}`}>
          <div className={styles.title_contents}>
            <p>Chargebacks</p>
          </div>
          <div className={styles.search_input_container}>
            <div className={styles.search_wrapper}>
              <SearchInput
                onChange={handleSearchChange}
                searchValue={searchInput}
              />
            </div>
            <div className={styles.month_picker_wrapper}>
              <MonthPicker
                variant="smallFilter"
                supportMobile
                onSubmit={handleMonthPickerChange}
                changeOnSubmit
                showYearFilters
                showActions
              />
            </div>
            <ButtonIcon
              bgColor="#EDEDF4"
              onClick={() => setFilterDrawerOpen(true)}
              className={styles.filter_button}
            >
              <span className={styles.filterIconWrapper}>
                <Filter />
                {filterCount > 0 && (
                  <RedDotBadge className={styles.filter_active} />
                )}
              </span>
              <span className={styles.filter_button_label}>
                Filter
                {filterCount > 0 ? `: ${filterCount}` : ""}
              </span>
            </ButtonIcon>
          </div>
        </div>

        <ChargebackStats />

        <ChargebackCharts />

        <div className={styles.desktop_table}>
          <ChargebackTabs />
        </div>

        <div className={styles.mobile_alert}>
          <MobileDesktopAlert />
        </div>
      </div>

      <FilterDrawer />
    </div>
  );
};

export default ChargebackPage;
